import type { BoardContent, CellSpec } from '../core/types';

/** Phone-legibility cap on cell text. Enforced by the validator (json_schema can't bound string length).
 *  22 (not 18) so real titles/names ("Million Dollar Baby") aren't truncated; the renderer shrink-fits. */
export const MAX_CELL_CHARS = 22;

/**
 * PERMISSIVE STRUCTURAL BACKSTOP — not the correctness floor.
 *
 * MIN_CORRECT/MIN_DECOY (3/6) only guarantee a board is *structurally playable* (enough of each
 * kind that the grid isn't degenerate). They are deliberately well below the real correctness floor
 * so this frozen client contract never rejects a legitimately-thin-but-verified board.
 *
 * The actual trust floor for ANY board the client plays lives upstream and is much stricter:
 *   - GENERATED boards (`/board`, `/daily`): the backend verifier module gates at
 *     MIN_VERIFIED_CORRECT=8 / MIN_VERIFIED_DECOY=12 (independent blind re-check) BEFORE ship;
 *     locked standard composition is 9-12 correct / 16-19 decoy.
 *   - SEED / BUNDLE / daily-fallback boards: pre-verified at build time and shipped baked-in.
 * Every board reaching the Core has therefore cleared the backend verifier or is a pre-verified
 * seed — this client-side validateBoardContent is only the last "is the response well-formed"
 * structural check (in the board client's fetchBoard/getDaily), never the trust gate.
 */
export const MIN_CORRECT = 3;
export const MIN_DECOY = 6;

/**
 * The json_schema handed to the M1 backend via output_config.format. The model authors only
 * `rule_statement` + `cells`; counts and char-limits are enforced by validateBoardContent (below),
 * because json_schema does not support minItems/maxItems/minLength.
 */
export const BOARD_JSON_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  properties: {
    rule_statement: { type: 'string' },
    cells: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: false,
        properties: {
          text: { type: 'string' },
          is_correct: { type: 'boolean' },
          difficulty: { type: 'integer', enum: [1, 2, 3] },
          explanation: { type: 'string' },
        },
        required: ['text', 'is_correct', 'explanation'],
      },
    },
  },
  required: ['rule_statement', 'cells'],
} as const;

export interface ValidationResult {
  ok: boolean;
  errors: string[];
}

/** Structural + count + char-limit + dedup validation. Used for the seed pack and (M1) LLM output. */
export function validateBoardContent(c: unknown): ValidationResult {
  const errors: string[] = [];
  const b = c as Partial<BoardContent>;
  if (!b || typeof b !== 'object') return { ok: false, errors: ['not an object'] };
  if (!b.topicNormalized) errors.push('missing topicNormalized');
  if (!b.ruleStatement) errors.push('missing ruleStatement');
  if (!Array.isArray(b.cells)) {
    errors.push('cells is not an array');
    return { ok: false, errors };
  }

  let correct = 0;
  let decoy = 0;
  const seen = new Set<string>();
  for (const [i, cell] of (b.cells as CellSpec[]).entries()) {
    if (!cell || typeof cell.text !== 'string' || cell.text.trim() === '') {
      errors.push(`cell ${i}: empty text`);
      continue;
    }
    if (cell.text.length > MAX_CELL_CHARS) errors.push(`cell ${i} "${cell.text}": > ${MAX_CELL_CHARS} chars`);
    if (cell.kind !== 'correct' && cell.kind !== 'decoy') errors.push(`cell ${i} "${cell.text}": kind must be correct|decoy`);
    const key = cell.text.trim().toLowerCase();
    if (seen.has(key)) errors.push(`duplicate cell text "${cell.text}"`);
    seen.add(key);
    if (cell.kind === 'correct') correct++;
    else if (cell.kind === 'decoy') decoy++;
  }
  if (correct < MIN_CORRECT) errors.push(`too few correct (${correct} < ${MIN_CORRECT})`);
  if (decoy < MIN_DECOY) errors.push(`too few decoy (${decoy} < ${MIN_DECOY})`);

  return { ok: errors.length === 0, errors };
}
